import { useEffect } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { apiGetMe, apiRefreshToken } from '../../api/auth';
import { ApiError } from '../../api/client';

const hadSession = (): boolean => {
  try {
    return localStorage.getItem('had_session') === '1';
  } catch {
    // localStorage blocked (private browsing, storage quota)
    return false;
  }
};

/**
 * Runs once on mount.
 *
 * Fast path (anonymous, no prior session):
 *   had_session flag absent → skip all network calls immediately.
 *
 * Token in sessionStorage:
 *   Validate with GET /auth/me. On 401, try silent refresh.
 *
 * No token but had_session set:
 *   New tab / session storage cleared — attempt silent refresh via
 *   httpOnly cookie to restore the session transparently.
 *
 * Renders nothing — purely a side-effect component.
 */
export default function AuthInitializer() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  useEffect(() => {
    // Anonymous user with no previous session — nothing to do.
    if (!accessToken && !hadSession()) return;

    if (!accessToken) {
      // had_session is set but sessionStorage was cleared (new tab, browser restart).
      // Attempt silent refresh via the httpOnly refresh cookie.
      apiRefreshToken()
        .then(({ accessToken: newToken }) =>
          apiGetMe().then((res) => setAuth(res.user, newToken)),
        )
        .catch(() => {
          // Refresh cookie expired or absent — clear the stale flag.
          localStorage.removeItem('had_session');
        });
      return;
    }

    apiGetMe()
      .then((res) => setAuth(res.user, accessToken))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          // Access token expired — try silent refresh.
          apiRefreshToken()
            .then(({ accessToken: newToken }) =>
              apiGetMe().then((res) => setAuth(res.user, newToken)),
            )
            .catch(() => clearAuth());
        } else {
          clearAuth();
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — runs once on mount

  return null;
}
